import React, { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import { apiClient } from '../../lib/apiClient'
import { FileText, Download } from '../icons/Icons'

interface Invoice {
  id: string
  stripe_invoice_id: string
  amount_paid: number
  currency: string
  status: string
  invoice_pdf_url: string | null
  billing_period_start: string
  billing_period_end: string
  created_at: string
}

export default function BillingHistory() {
  const { organization } = useAuth()
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadInvoices()
  }, [organization])

  const loadInvoices = async () => {
    if (!organization) return

    setLoading(true)

    try {
      const data = await apiClient.get<Invoice[]>(`/orgs/${organization.id}/invoices`)
      setInvoices(data || [])
    } catch (err) {
      console.error('Error loading invoices:', err)
    }

    setLoading(false)
  }

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return { bg: '#dff6dd', color: '#107c10' }
      case 'open':
        return { bg: '#fff4ce', color: '#ca5010' }
      case 'void':
      case 'uncollectible':
        return { bg: '#f3f2f1', color: '#605e5c' }
      default:
        return { bg: '#f3f2f1', color: '#323130' }
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#605e5c' }}>
        Loading billing history...
      </div>
    )
  }

  if (invoices.length === 0) {
    return (
      <div style={{
        padding: 40,
        textAlign: 'center',
        color: '#605e5c',
        border: '1px dashed #c8c6c4',
        borderRadius: 4,
      }}>
        <FileText size={32} style={{ marginBottom: 12, opacity: 0.5 }} />
        <div style={{ fontSize: 14 }}>No invoices yet</div>
        <div style={{ fontSize: 13, marginTop: 4 }}>
          Your billing history will appear here once you have been charged
        </div>
      </div>
    )
  }

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          background: '#fff',
          border: '1px solid #edebe9',
          borderRadius: 4,
        }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #edebe9', background: '#faf9f8' }}>
              <th style={{
                padding: '12px 16px',
                textAlign: 'left',
                fontSize: 12,
                fontWeight: 600,
                color: '#605e5c',
              }}>
                Date
              </th>
              <th style={{
                padding: '12px 16px',
                textAlign: 'left',
                fontSize: 12,
                fontWeight: 600,
                color: '#605e5c',
              }}>
                Billing Period
              </th>
              <th style={{
                padding: '12px 16px',
                textAlign: 'left',
                fontSize: 12,
                fontWeight: 600,
                color: '#605e5c',
              }}>
                Amount
              </th>
              <th style={{
                padding: '12px 16px',
                textAlign: 'left',
                fontSize: 12,
                fontWeight: 600,
                color: '#605e5c',
              }}>
                Status
              </th>
              <th style={{
                padding: '12px 16px',
                textAlign: 'left',
                fontSize: 12,
                fontWeight: 600,
                color: '#605e5c',
              }}>
                Invoice
              </th>
            </tr>
          </thead>
          <tbody>
            {invoices.map(invoice => {
              const statusStyle = getStatusColor(invoice.status)
              return (
                <tr key={invoice.id} style={{ borderBottom: '1px solid #edebe9' }}>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#323130' }}>
                    {new Date(invoice.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#605e5c' }}>
                    {new Date(invoice.billing_period_start).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })}
                    {' - '}
                    {new Date(invoice.billing_period_end).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#323130', fontWeight: 600 }}>
                    {formatCurrency(invoice.amount_paid, invoice.currency)}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: 3,
                      fontSize: 11,
                      fontWeight: 600,
                      background: statusStyle.bg,
                      color: statusStyle.color,
                    }}>
                      {invoice.status.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {invoice.invoice_pdf_url ? (
                      <a
                        href={invoice.invoice_pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          color: '#0078d4',
                          textDecoration: 'none',
                          fontSize: 13,
                        }}
                      >
                        <Download size={14} />
                        PDF
                      </a>
                    ) : (
                      <span style={{ fontSize: 13, color: '#a19f9d' }}>—</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div style={{
        marginTop: 20,
        padding: 12,
        background: '#f3f2f1',
        borderRadius: 4,
        fontSize: 13,
        color: '#605e5c',
      }}>
        All invoices are generated and processed by Stripe. If you have questions about a specific charge, please contact support.
      </div>
    </div>
  )
}
